import { useState } from "react";
import { useDiscordSdk } from "../hooks/useDiscordSdk";
import { ConsoleGrid } from "./ConsoleGrid";
import { RomUpload } from "./RomUpload";
import { Emulator } from "./Emulator";
import type { GameConsole } from "../consoles";

interface ActivityProps {
  clientId: string;
}

/**
 * The in-Discord flow: Discord SDK gate (error / connecting / ready) → console
 * grid → ROM upload → emulator. Fills the viewport; the wrapper owns its own
 * full-screen sizing so the landing page can stay scrollable elsewhere.
 */
export function Activity({ clientId }: ActivityProps) {
  const { ready, user, error } = useDiscordSdk(clientId);
  const [selectedConsole, setSelectedConsole] = useState<GameConsole | null>(null);
  const [rom, setRom] = useState<File | null>(null);

  return (
    <div className="h-screen overflow-hidden">
      <ActivityInner
        ready={ready}
        user={user}
        error={error}
        selectedConsole={selectedConsole}
        setSelectedConsole={setSelectedConsole}
        rom={rom}
        setRom={setRom}
      />
    </div>
  );
}

interface ActivityInnerProps {
  ready: boolean;
  user: { username: string; global_name: string | null } | null;
  error: Error | null;
  selectedConsole: GameConsole | null;
  setSelectedConsole: (c: GameConsole | null) => void;
  rom: File | null;
  setRom: (f: File | null) => void;
}

function ActivityInner({
  ready,
  user,
  error,
  selectedConsole,
  setSelectedConsole,
  rom,
  setRom,
}: ActivityInnerProps) {
  // --- Discord gating ---

  if (error) {
    return (
      <div className="flex items-center justify-center h-full p-6">
        <div className="text-center max-w-sm">
          <div className="text-4xl mb-4">:/</div>
          <p className="text-text-secondary">Failed to connect to Discord</p>
          <p className="text-text-muted text-sm mt-2">{error.message}</p>
        </div>
      </div>
    );
  }

  if (!ready) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-[#5865F2] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-text-secondary text-sm">Connecting to Discord...</p>
        </div>
      </div>
    );
  }

  // --- Ready ---

  if (!selectedConsole) {
    return <ConsoleGrid onPick={setSelectedConsole} />;
  }

  if (!rom) {
    return (
      <RomUpload
        console={selectedConsole}
        onPick={setRom}
        onBack={() => setSelectedConsole(null)}
      />
    );
  }

  const username = user?.global_name ?? user?.username ?? "Player";

  return (
    <div className="flex flex-col h-full">
      <header className="flex items-center justify-between gap-2 bg-surface-2 border-b border-border px-4 py-2">
        <span className="text-text-secondary text-sm truncate">
          Playing as <span className="text-text-primary font-medium">{username}</span> on{" "}
          <span className="text-text-primary font-medium">{selectedConsole.name}</span>
        </span>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={() => {
              setRom(null);
              setSelectedConsole(null);
            }}
            className="text-xs font-medium rounded-md bg-surface-3 hover:bg-surface-4 transition-colors px-3 py-1.5 text-text-primary"
          >
            Change console
          </button>
          <button
            type="button"
            onClick={() => setRom(null)}
            className="text-xs font-medium rounded-md bg-[#5865F2] hover:bg-[#4752c4] transition-colors px-3 py-1.5 text-white"
          >
            Load another ROM
          </button>
        </div>
      </header>
      <div className="flex-1 min-h-0">
        <Emulator key={rom.name} file={rom} core={selectedConsole.core} />
      </div>
    </div>
  );
}
